import EventEmitter from 'eventemitter3';
import { uid } from '../../../../utils/data/uid.mjs';
import { TextureSource } from '../texture/sources/TextureSource.mjs';
import { Texture } from '../texture/Texture.mjs';

"use strict";
const _RenderTarget = class _RenderTarget extends EventEmitter {
  /**
   * @param options - Options for creating a render target, or a WebGPU-flavored descriptor.
   */
  constructor(options = {}) {
    super();
    /** unique id for this render target */
    this.uid = uid("renderTarget");
    /**
     * An array of attachments that define exactly how the GPU should render to the color textures.
     * This includes the texture itself, as well as load/store operations and clear values.
     */
    this.colorAttachments = [];
    this.dirtyId = 0;
    this.isRoot = false;
    this._size = new Float32Array(2);
    /** if true, then when the render target is destroyed, it will destroy all the textures that were created for it. */
    this._managedColorTextures = false;
    /** depth capability requested for this target — via options, attachment format, or the mask system @internal */
    this._depth = false;
    /** stencil capability requested for this target — via options, attachment format, or the mask system @internal */
    this._stencil = false;
    this._colorTextures = null;
    const descriptor = "colorAttachments" in options ? options : this._normalizeOptions(options);
    this.isRoot = descriptor.isRoot ?? false;
    this.label = descriptor.label;
    this.colorAttachments = descriptor.colorAttachments;
    this.depthStencilAttachment = descriptor.depthStencilAttachment;
    if (this.depthStencilAttachment) {
      const format = this.depthStencilAttachment.texture.format;
      this._depth || (this._depth = format.includes("depth"));
      this._stencil || (this._stencil = format.includes("stencil"));
    }
    if (this.colorAttachments.length === 0 && !this.depthStencilAttachment) {
      throw new Error("[RenderTarget] no color textures or depth textures were provided. Provide a depthStencilTexture or set depth/stencil to true when using colorTextures: 0.");
    }
    if (this.colorAttachments.length > 0) {
      const colorSource = this.colorTexture;
      this.resize(colorSource.width, colorSource.height, colorSource._resolution);
    }
    if (this.sizeSource) {
      this.sizeSource.on("resize", this.onSourceResize, this);
    }
  }
  _normalizeOptions(options) {
    const opts = { ..._RenderTarget.defaultOptions, ...options };
    const colorAttachments = [];
    let depthStencilAttachment;
    if (typeof opts.colorTextures === "number") {
      if (opts.colorTextures > 0) {
        this._managedColorTextures = true;
        for (let i = 0; i < opts.colorTextures; i++) {
          colorAttachments.push({
            texture: new TextureSource({
              width: opts.width,
              height: opts.height,
              resolution: opts.resolution,
              antialias: opts.antialias
            }),
            loadOp: "clear",
            storeOp: "store"
          });
        }
      }
    } else {
      opts.colorTextures.forEach((texture) => {
        colorAttachments.push({
          texture: texture.source,
          loadOp: "clear",
          storeOp: "store"
        });
      });
    }
    const wantsDepthStencilTexture = opts.depthStencilTexture === true;
    this._depth = !!(opts.depth || wantsDepthStencilTexture);
    this._stencil = !!(opts.stencil || wantsDepthStencilTexture);
    if (opts.depthStencilTexture instanceof Texture || opts.depthStencilTexture instanceof TextureSource) {
      if (opts.isRoot) {
        throw new Error("[RenderTarget] cannot attach a depth-stencil texture to the screen \u2014 the canvas owns its own depth/stencil buffers. Render to a texture target instead.");
      }
      depthStencilAttachment = {
        texture: opts.depthStencilTexture.source
      };
    } else if (wantsDepthStencilTexture && !opts.isRoot || (opts.stencil || opts.depth) && colorAttachments.length === 0) {
      depthStencilAttachment = this._createDepthStencilTexture(opts.width, opts.height, opts.resolution);
    }
    return {
      colorAttachments,
      depthStencilAttachment,
      isRoot: opts.isRoot,
      label: opts.label
    };
  }
  get size() {
    const _size = this._size;
    _size[0] = this.pixelWidth;
    _size[1] = this.pixelHeight;
    return _size;
  }
  get width() {
    return this.sizeSource.width;
  }
  get height() {
    return this.sizeSource.height;
  }
  get pixelWidth() {
    return this.sizeSource.pixelWidth;
  }
  get pixelHeight() {
    return this.sizeSource.pixelHeight;
  }
  get resolution() {
    return this.sizeSource._resolution;
  }
  /**
   * An array of textures that can be written to by the GPU - mostly this has one texture in Pixi, but you could
   * write to multiple if required! (eg deferred lighting).
   * This is a backwards-compatible getter that extracts the textures from `colorAttachments`.
   */
  get colorTextures() {
    this._colorTextures || (this._colorTextures = this.colorAttachments.map((a) => a.texture));
    return this._colorTextures;
  }
  /** The stencil and depth buffer will write to this texture in WebGPU. */
  get depthStencilTexture() {
    return this.depthStencilAttachment?.texture ?? null;
  }
  /** Whether this target provides a depth buffer — requested via options or implied by its attachment's format. */
  get depth() {
    return this._depth;
  }
  /** Whether this target provides a stencil buffer — requested via options or implied by its attachment's format. */
  get stencil() {
    return this._stencil;
  }
  get colorTexture() {
    return this.colorAttachments[0]?.texture;
  }
  /**
   * The texture that drives size, resolution, and resize events.
   * For standard targets this is `colorAttachments[0].texture`;
   * for depth-only targets it is `depthStencilAttachment.texture`.
   */
  get sizeSource() {
    return this.colorAttachments[0]?.texture ?? this.depthStencilAttachment?.texture;
  }
  onSourceResize(source) {
    this.resize(source.width, source.height, source._resolution, true);
  }
  /**
   * This will ensure a depthStencil texture is created for this render target.
   * Most likely called by the mask system to make sure we have stencil buffer added.
   * @internal
   */
  ensureDepthStencilTexture() {
    this._createDepthStencilTexture(this.sizeSource.width, this.sizeSource.height, this.sizeSource._resolution);
    this._depth = true;
    this._stencil = true;
  }
  resize(width, height, resolution = this.resolution, skipColorTexture = false) {
    this.dirtyId++;
    this.colorAttachments.forEach((colorAttachment, i) => {
      if (skipColorTexture && i === 0) return;
      colorAttachment.texture.resize(width, height, resolution);
    });
    if (this.depthStencilAttachment) {
      if (skipColorTexture && this.colorAttachments.length === 0) return;
      this.depthStencilAttachment.texture.resize(width, height, resolution);
    }
  }
  destroy() {
    if (!this.colorAttachments && !this.depthStencilAttachment) return;
    this.emit("destroy", this);
    this.sizeSource.off("resize", this.onSourceResize, this);
    if (this._managedColorTextures) {
      this.colorAttachments.forEach((attachment) => {
        attachment.texture.destroy();
      });
    }
    if (this.depthStencilAttachment) {
      this.depthStencilAttachment.texture.destroy();
      delete this.depthStencilAttachment;
    }
    this.colorAttachments = null;
    this._colorTextures = null;
    this.removeAllListeners();
  }
  /**
   * The single recipe for internally-created depth-stencil textures.
   * @param width
   * @param height
   * @param resolution
   */
  _createDepthStencilTexture(width, height, resolution) {
    this.depthStencilAttachment ?? (this.depthStencilAttachment = {
      texture: new TextureSource({
        width,
        height,
        resolution,
        format: "depth24plus-stencil8",
        autoGenerateMipmaps: false,
        antialias: false,
        mipLevelCount: 1
        // sampleCount: handled by the render target system..
      })
    });
    return this.depthStencilAttachment;
  }
};
/**
 * emits when the render target is destroyed, before its attachments are released.
 * letting the renderer know that it needs to free the framebuffers and MSAA buffers it built for it
 * @event destroy
 */
/** The default options for a render target */
_RenderTarget.defaultOptions = {
  /** the width of the RenderTarget */
  width: 0,
  /** the height of the RenderTarget */
  height: 0,
  /** the resolution of the RenderTarget */
  resolution: 1,
  /** an array of textures, or a number indicating how many color textures there should be */
  colorTextures: 1,
  /** should this render target have a stencil buffer? */
  stencil: false,
  /** should this render target have a depth buffer? */
  depth: false,
  /** should this render target be antialiased? */
  antialias: false,
  // save on perf by default!
  /** is this a root element, true if this is gl context owners render target */
  isRoot: false
};
let RenderTarget = _RenderTarget;

export { RenderTarget };
//# sourceMappingURL=RenderTarget.mjs.map
